import React from 'react';
import { createPortal } from 'react-dom';

// Human-readable labels for the regex pattern keys the backend emits.
const SENSITIVE_PATTERN_LABELS = {
  absolute_efficacy_claim: 'Absolute efficacy / safety claim',
  regulatory_recommendation: 'Regulatory / prescribing recommendation',
  bare_numeric_no_citation: 'Numeric claim without citation',
};

const prettySensitivePattern = (pattern) =>
  SENSITIVE_PATTERN_LABELS[pattern] || pattern || 'Flagged phrase';

const AgentWarningsModal = ({
  sensitiveFlags = [],
  groundingFlags = [],
  agentName,
  onClose,
}) => {
  const sensitive = Array.isArray(sensitiveFlags) ? sensitiveFlags : [];
  const grounding = Array.isArray(groundingFlags) ? groundingFlags : [];
  const totalCount = sensitive.length + grounding.length;

  return createPortal(
    <div
      className="ev-modal-overlay d-flex align-items-center justify-content-center p-3"
      style={{ zIndex: 'var(--num-1000000000)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ev-card-4 d-flex flex-column overflow-hidden vh-80 w-50">
        {/* header */}
        <div className="d-flex align-items-center justify-content-between ev-padding-small border-bottom flex-shrink-0">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-exclamation-triangle-fill text-warning"></i>
              <span className="ev-bold-med ev-reg-p-dark">Review Warnings</span>
              <span className="ev-message-card3 ev-btn-xsmall">{totalCount}</span>
            </div>
            {agentName && <span className="ev-message-card1 ev-btn-xsmall">{agentName}</span>}
          </div>
          <button
            type="button"
            className="ev-btn-light-fill ev-btn-xsmall ev-radius-xlarge"
            onClick={onClose}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* body */}
        <div className="ev-padding-small scrollable-element flex-grow-1 pt-2">
          {totalCount === 0 && (
            <div className="ev-card-2 text-center ev-padding-reg ev-radius-small">
              <i className="bi bi-check2-circle ev-fs-25 ev-small-p-blue d-block mb-2"></i>
              <p className="ev-small-p-dark mb-0">No warnings for this response.</p>
            </div>
          )}

          {/* Grounding flags */}
          {grounding.length > 0 && (
            <div className="mb-3">
              <div className="ev-bold-med ev-small-p-blue mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-search"></i>
                Grounding issues
                <span className="ev-message-card3 ev-btn-xsmall">{grounding.length}</span>
              </div>

              {grounding.map((f, i) => (
                <div key={`grounding-${i}`} className="ev-card-2 ev-radius-xsmall p-2 mb-2">
                  <p className="ev-small-p-dark mb-1">{f.claim_text}</p>
                  <div className="d-flex flex-wrap align-items-center gap-1 mb-1">
                    {f.doc_id != null && (
                      <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">
                        doc_{f.doc_id}
                      </span>
                    )}
                    {f.grounding_score != null && (
                      <span className="ev-message-card3 ev-btn-xsmall">
                        score {f.grounding_score}
                      </span>
                    )}
                    {f.threshold != null && (
                      <span className="ev-message-card3 ev-btn-xsmall">
                        threshold {f.threshold}
                      </span>
                    )}
                  </div>
                  {f.best_chunk_preview && (
                    <div
                      className="ev-card-8 p-2 ev-radius-xsmall"
                      style={{ borderLeft: '3px solid var(--color1)' }}
                    >
                      <span className="ev-bold-med ev-xsmall-p-dark">Closest source text: </span>
                      <span className="ev-xsmall-p-dark">{f.best_chunk_preview}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Sensitive-output flags */}
          {sensitive.length > 0 && (
            <div className="mb-1">
              <div className="ev-bold-med ev-small-p-blue mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-shield-exclamation"></i>
                Sensitive output
                <span className="ev-message-card3 ev-btn-xsmall">{sensitive.length}</span>
              </div>

              {sensitive.map((f, i) => (
                <div key={`sensitive-${i}`} className="ev-card-2 ev-radius-xsmall p-2 mb-2">
                  <div className="mb-1">
                    <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">
                      {prettySensitivePattern(f.pattern)}
                    </span>
                  </div>
                  <p className="ev-small-p-dark mb-0">{f.matched_text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AgentWarningsModal;